import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, Briefcase, Receipt, CurrencyGbp, ChatTeardropDots, Envelope, ShieldCheck, ChartBar, ChartLine, X, Check, Rocket, Warning, Image as ImageIcon, Copy, UploadSimple, Trash, Question, PencilSimple, Plus, EyeSlash, Eye, EnvelopeOpen, PaperPlaneTilt, Archive, ArrowLeft, Gift, ChatCircleText, Article } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import VisitorMap from "../components/admin/VisitorMap";
import AdminSMSTab from "../components/admin/AdminSMSTab";
import AdminBlogTab from "../components/admin/AdminBlogTab";

const TABS = [
  { id: "overview", label: "Overview", icon: ChartBar },
  { id: "analytics", label: "Live visitors", icon: ChartLine },
  { id: "trends", label: "Trends", icon: ChartLine },
  { id: "users", label: "Users", icon: Users },
  { id: "jobs", label: "Jobs", icon: Briefcase },
  { id: "quotes", label: "Quotes", icon: ChatTeardropDots },
  { id: "verifications", label: "Verifications", icon: ShieldCheck },
  { id: "payments", label: "Payments", icon: CurrencyGbp },
  { id: "signups", label: "Launch list", icon: Envelope },
  { id: "waitlist", label: "Waitlist", icon: Rocket },
  { id: "disputes", label: "Disputes", icon: Warning },
  { id: "inbox", label: "Inbox", icon: EnvelopeOpen },
  { id: "vouchers", label: "Vouchers", icon: Gift },
  { id: "sms", label: "SMS", icon: ChatCircleText },
  { id: "blog", label: "Blog", icon: Article },
  { id: "assets", label: "Assets", icon: ImageIcon },
  { id: "faqs", label: "FAQ", icon: Question },
];


function ReconcileStripeCard() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (!email.trim()) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`/admin/reconcile-stripe/${encodeURIComponent(email.trim().toLowerCase())}`);
      setResult({ ok: true, data });
      toast.success("Subscription reconciled — trader activated");
    } catch (e) {
      setResult({ ok: false, msg: formatApiError(e) });
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };
  return (
    <div className="brand-card p-5" data-testid="reconcile-stripe-card">
      <div className="font-display font-extrabold text-lg" style={{ color: "var(--brand-green)" }}>Reconcile a trader with Stripe</div>
      <p className="text-sm text-[var(--text-muted)] mt-1">Paid through Stripe but still showing as unactivated? Enter their email — we&apos;ll pull the live subscription and activate them.</p>
      <div className="flex flex-col sm:flex-row gap-2 mt-3">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="trader@example.com"
          className="brand-input flex-1"
          data-testid="reconcile-email-input"
        />
        <button onClick={run} disabled={busy || !email.trim()} className="btn-primary" data-testid="reconcile-run-btn">
          {busy ? "Reconciling…" : "Reconcile"}
        </button>
      </div>
      {result && result.ok && (
        <div className="mt-3 text-sm rounded-md p-3" style={{ background: "#ECFDF5", color: "#065F46" }} data-testid="reconcile-success">
          ✅ Activated — renews {new Date(result.data.subscription_renews_at).toLocaleDateString()}
        </div>
      )}
      {result && !result.ok && (
        <div className="mt-3 text-sm rounded-md p-3" style={{ background: "#FEF2F2", color: "#991B1B" }} data-testid="reconcile-error">
          {result.msg}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div className="brand-card p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">{label}</div>
      <div className="font-display font-extrabold text-3xl mt-1" style={{ color: accent || "var(--brand-green)" }}>{value}</div>
      {sub && <div className="text-xs text-[var(--text-muted)] mt-1">{sub}</div>}
    </div>
  );
}

function DemoFunnelCard() {
  const [d, setD] = React.useState(null);
  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/demo/funnel");
        setD(data);
      } catch (_) { /* noop */ }
    })();
  }, []);
  if (!d) return null;
  const c = d.counts || {};
  return (
    <div className="brand-card p-5" data-testid="demo-funnel-card">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs font-bold uppercase tracking-widest text-[var(--brand-orange)]">Demo funnel</span>
        <span className="text-xs text-[var(--text-muted)]">· conversion: <strong className="text-[var(--brand-green)]">{d.conversion_rate_pct}%</strong></span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <div className="text-xs text-[var(--text-muted)]">Trader demos started</div>
          <div className="font-display font-extrabold text-2xl text-[var(--brand-green)] mt-0.5">{c.demo_started_trader || 0}</div>
        </div>
        <div>
          <div className="text-xs text-[var(--text-muted)]">Customer demos started</div>
          <div className="font-display font-extrabold text-2xl text-[var(--brand-green)] mt-0.5">{c.demo_started_customer || 0}</div>
        </div>
        <div>
          <div className="text-xs text-[var(--text-muted)]">Clicked "Join waitlist"</div>
          <div className="font-display font-extrabold text-2xl text-[var(--brand-orange)] mt-0.5">{c.demo_clicked_join_waitlist || 0}</div>
        </div>
        <div>
          <div className="text-xs text-[var(--text-muted)]">Exited demo</div>
          <div className="font-display font-extrabold text-2xl text-[var(--text-muted)] mt-0.5">{c.demo_exited || 0}</div>
        </div>
      </div>
    </div>
  );
}

function Table({ columns, rows, empty = "No data yet" }) {
  if (!rows || rows.length === 0) return <p className="text-[var(--text-muted)] py-6 text-center">{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-[var(--text-muted)] border-b border-[var(--border-stone)]">
            {columns.map((c) => <th key={c.key} className="px-3 py-2 font-semibold">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-[var(--border-stone)] last:border-b-0 hover:bg-[#FAF8F2]">
              {columns.map((c) => (
                <td key={c.key} className="px-3 py-2 align-top">{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" });
}

function fmtGBP(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return `£${Number(n).toFixed(2)}`;
}

function Badge({ children, color = "green" }) {
  const map = {
    green: "bg-[rgba(17,82,59,0.1)] text-[var(--brand-green)]",
    orange: "bg-[rgba(226,109,41,0.12)] text-[var(--brand-orange)]",
    grey: "bg-gray-100 text-gray-600",
    red: "bg-red-100 text-red-700",
    emerald: "bg-emerald-100 text-emerald-700",
  };
  return <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold ${map[color] || map.green}`}>{children}</span>;
}

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState({});
  const [busy, setBusy] = useState(false);

  const isAdmin = user && user.role === "admin";

  const load = async (which) => {
    setBusy(true);
    try {
      if (which === "trends") {
        const { data: d } = await api.get(`/admin/timeseries?days=30`);
        setData((s) => ({ ...s, trends: d }));
      } else if (which === "verifications") {
        const { data: d } = await api.get(`/admin/verifications`);
        setData((s) => ({ ...s, verifications: d }));
      } else if (which === "waitlist") {
        const { data: d } = await api.get(`/admin/waitlist`);
        setData((s) => ({ ...s, waitlist: d }));
      } else if (which === "disputes") {
        const { data: d } = await api.get(`/admin/disputes`);
        setData((s) => ({ ...s, disputes: d }));
      } else if (which === "assets") {
        const { data: d } = await api.get(`/admin/assets`);
        setData((s) => ({ ...s, assets: d }));
      } else if (which === "inbox") {
        const { data: d } = await api.get(`/admin/contact`);
        setData((s) => ({ ...s, inbox: d }));
      } else if (which === "vouchers") {
        const { data: d } = await api.get(`/admin/waitlist/vouchers`);
        setData((s) => ({ ...s, vouchers: d }));
      } else if (which === "faqs") {
        const { data: d } = await api.get(`/admin/faqs`);
        setData((s) => ({ ...s, faqs: d }));
      } else if (which === "analytics") {
        const [{ data: a }, { data: l }, { data: loc }, { data: mp }] = await Promise.all([
          api.get(`/admin/analytics/overview`),
          api.get(`/admin/analytics/live`),
          api.get(`/admin/analytics/locations`),
          api.get(`/admin/analytics/map-points`),
        ]);
        setData((s) => ({ ...s, analytics: a, analytics_live: l, analytics_loc: loc, analytics_map: mp }));
      } else {
        const { data: d } = await api.get(`/admin/${which}`);
        setData((s) => ({ ...s, [which]: d }));
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const approveVerification = async (id) => {
    try {
      await api.post(`/admin/verifications/${id}/approve`);
      toast.success("Trader approved as Verified ✅");
      await load("verifications");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const rejectVerification = async (id) => {
    const reason = prompt("Reason for rejection (optional):") || "";
    try {
      await api.post(`/admin/verifications/${id}/reject${reason ? `?reason=${encodeURIComponent(reason)}` : ""}`);
      toast.success("Submission rejected");
      await load("verifications");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    if (isAdmin) load(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, isAdmin]);

  if (loading) return <Layout><div className="p-10 text-[var(--text-muted)]">Loading…</div></Layout>;
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (!isAdmin) return (
    <Layout>
      <div className="max-w-xl mx-auto p-10 text-center">
        <ShieldCheck size={48} weight="duotone" className="mx-auto text-[var(--brand-orange)]" />
        <h1 className="font-display font-extrabold text-2xl mt-3" style={{ color: "var(--brand-green)" }}>Admin only</h1>
        <p className="text-[var(--text-muted)] mt-2">This area is restricted. Log in with the admin account.</p>
      </div>
    </Layout>
  );

  const ov = data.overview;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8" data-testid="admin-dashboard">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl" style={{ color: "var(--brand-green)" }}>Admin dashboard</h1>
            <p className="text-[var(--text-muted)] mt-1">MyHandyHub — internal command centre</p>
          </div>
          <Badge color="green"><ShieldCheck size={11} weight="fill" className="inline -mt-0.5 mr-1" /> Signed in as {user.email}</Badge>
        </div>

        {/* Tabs */}
        <div className="mt-6 flex gap-1 overflow-x-auto scrollbar-thin border-b border-[var(--border-stone)]" data-testid="admin-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${
                  active
                    ? "border-[var(--brand-orange)] text-[var(--brand-green)]"
                    : "border-transparent text-[var(--text-muted)] hover:text-[var(--brand-green)]"
                }`}
              >
                <Icon size={16} weight={active ? "duotone" : "regular"} /> {t.label}
              </button>
            );
          })}
        </div>

        {busy && <p className="text-[var(--text-muted)] mt-4">Loading…</p>}

        {/* OVERVIEW */}

        {/* ANALYTICS / LIVE VISITORS */}
        {tab === "analytics" && (
          <AnalyticsTab data={data.analytics} live={data.analytics_live} loc={data.analytics_loc} mapData={data.analytics_map} onRefresh={() => load("analytics")} />
        )}

        {tab === "sms" && <AdminSMSTab />}
        {tab === "blog" && <AdminBlogTab />}

        {tab === "overview" && ov && (
          <div className="mt-6 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Total users" value={ov.users.total} sub={`${ov.users.customers} customers · ${ov.users.tradespeople} trades`} />
              <StatCard label="Active subs" value={ov.users.tradespeople_active_subscription} sub={`of ${ov.users.tradespeople} traders`} accent="var(--brand-orange)" />
              <StatCard label="Revenue" value={fmtGBP(ov.revenue.total_gbp)} sub={`${ov.revenue.paid_transactions} payments`} />
              <StatCard label="Reserve buffer" value={fmtGBP(ov.revenue.reserve_buffer_gbp)} sub="10% of revenue" />
              <StatCard label="Jobs posted" value={ov.jobs.total} sub={`${ov.jobs.open} open · ${ov.jobs.completed} done`} />
              <StatCard label="Quotes sent" value={ov.quotes.total} sub={`${ov.quotes.accepted} accepted`} />
              <StatCard label="Reviews" value={ov.reviews_total} />
              <StatCard label="Launch list" value={ov.signups_total} sub="pre-launch emails" />
            </div>
            <DemoFunnelCard />
          </div>
        )}

        {/* USERS */}
        {tab === "users" && (
          <div className="mt-6 brand-card p-4">
            <Table
              rows={data.users || []}
              columns={[
                { key: "name", label: "Name", render: r => <span className="font-semibold text-[var(--brand-green)]">{r.name}</span> },
                { key: "email", label: "Email" },
                { key: "role", label: "Role", render: r => <Badge color={r.role === "tradesperson" ? "orange" : r.role === "admin" ? "grey" : "green"}>{r.role}</Badge> },
                { key: "postcode", label: "Postcode" },
                { key: "trades", label: "Trades", render: r => (r.trades || []).join(", ") || "—" },
                { key: "subscription_status", label: "Sub", render: r => r.role === "tradesperson" ? <Badge color={r.subscription_status === "active" ? "emerald" : "grey"}>{r.subscription_status}</Badge> : "—" },
                { key: "auth_provider", label: "Auth" },
                { key: "created_at", label: "Joined", render: r => fmtDate(r.created_at) },
              ]}
            />
          </div>
        )}

        {/* JOBS */}
        {tab === "jobs" && (
          <div className="mt-6 brand-card p-4">
            <Table
              rows={data.jobs || []}
              columns={[
                { key: "title", label: "Title", render: r => <span className="font-semibold text-[var(--brand-green)]">{r.title}</span> },
                { key: "trade", label: "Trade" },
                { key: "postcode", label: "Postcode" },
                { key: "customer_name", label: "Customer" },
                { key: "budget", label: "Budget", render: r => r.budget ? fmtGBP(r.budget) : "—" },
                { key: "quote_count", label: "Quotes" },
                { key: "status", label: "Status", render: r => <Badge color={r.status === "open" ? "orange" : r.status === "completed" ? "emerald" : "green"}>{r.status}</Badge> },
                { key: "created_at", label: "Posted", render: r => fmtDate(r.created_at) },
              ]}
            />
          </div>
        )}

        {/* QUOTES */}
        {tab === "quotes" && (
          <div className="mt-6 brand-card p-4">
            <Table
              rows={data.quotes || []}
              columns={[
                { key: "tradesperson_name", label: "Trader", render: r => <span className="font-semibold text-[var(--brand-green)]">{r.tradesperson_business || r.tradesperson_name}</span> },
                { key: "amount", label: "Amount", render: r => fmtGBP(r.amount) },
                { key: "status", label: "Status", render: r => <Badge color={r.status === "accepted" ? "emerald" : r.status === "declined" ? "red" : "orange"}>{r.status}</Badge> },
                { key: "job_id", label: "Job ID", render: r => <code className="text-xs">{(r.job_id || "").slice(0, 16)}…</code> },
                { key: "created_at", label: "Sent", render: r => fmtDate(r.created_at) },
              ]}
            />
          </div>
        )}

        {/* PAYMENTS */}
        {tab === "payments" && (
          <div className="mt-6 space-y-4">
            <ReconcileStripeCard />
            <div className="brand-card p-4">
            <Table
              rows={data.payments || []}
              columns={[
                { key: "email", label: "Customer" },
                { key: "plan", label: "Plan", render: r => <Badge color="green">{r.plan}</Badge> },
                { key: "amount", label: "Amount", render: r => fmtGBP(r.amount) },
                { key: "reserve_amount", label: "Reserve", render: r => fmtGBP(r.reserve_amount) },
                { key: "payment_status", label: "Status", render: r => <Badge color={r.payment_status === "paid" ? "emerald" : "orange"}>{r.payment_status}</Badge> },
                { key: "session_id", label: "Session", render: r => <code className="text-xs">{(r.session_id || "").slice(0, 12)}…</code> },
                { key: "created_at", label: "Date", render: r => fmtDate(r.created_at) },
              ]}
            />
            </div>
          </div>
        )}

        {/* WAITLIST */}
        {tab === "waitlist" && (
          <div className="mt-6 space-y-4">
            <div className="brand-card p-5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-display font-extrabold text-lg" style={{ color: "var(--brand-green)" }}>Pre-launch waiting list</div>
                <p className="text-sm text-[var(--text-muted)] mt-1">
                  Signups from the homepage waitlist landing page. {data.waitlist?.count || 0} on the list.
                </p>
              </div>
              <div className="flex gap-2">
                <a
                  href={`${api.defaults.baseURL}/admin/waitlist/export.csv?kind=customer`}
                  target="_blank" rel="noreferrer"
                  className="btn-ghost"
                  data-testid="waitlist-export-customers"
                >Export customers</a>
                <a
                  href={`${api.defaults.baseURL}/admin/waitlist/export.csv?kind=trade`}
                  target="_blank" rel="noreferrer"
                  className="btn-ghost"
                  data-testid="waitlist-export-trades"
                >Export trades</a>
              </div>
            </div>
            <div className="brand-card p-4">
              <Table
                empty="No waitlist signups yet — share myhandyhub.co.uk to get started"
                rows={data.waitlist?.rows || []}
                columns={[
                  { key: "kind", label: "Type", render: r => <Badge color={r.kind === "trade" ? "orange" : "green"}>{r.kind}</Badge> },
                  { key: "first_name", label: "Name", render: r => <span className="font-semibold">{r.first_name}{r.business_name ? ` · ${r.business_name}` : ""}</span> },
                  { key: "email", label: "Email" },
                  { key: "mobile", label: "Mobile" },
                  { key: "postcode", label: "Postcode" },
                  { key: "primary_trade", label: "Trade" },
                  { key: "created_at", label: "Signed up", render: r => fmtDate(r.created_at) },
                ]}
              />
            </div>
          </div>
        )}

        {/* DISPUTES */}
        {tab === "disputes" && (
          <div className="mt-6 space-y-4">
            <div className="brand-card p-5">
              <div className="font-display font-extrabold text-lg flex items-center gap-2" style={{ color: "var(--brand-green)" }}>
                <Warning size={20} weight="duotone" /> Active disputes
              </div>
              <p className="text-sm text-[var(--text-muted)] mt-1">
                {(data.disputes?.count || 0)} job{(data.disputes?.count || 0) === 1 ? "" : "s"} flagged for review by a customer or trade.
              </p>
            </div>
            <div className="brand-card p-4">
              <Table
                empty="No disputes — keep it up 🎉"
                rows={data.disputes?.rows || []}
                columns={[
                  { key: "title", label: "Job", render: r => (
                    <Link to={`/jobs/${r.job_id}`} className="font-semibold underline">{r.title || r.job_id}</Link>
                  ) },
                  { key: "dispute_raised_by_role", label: "Raised by", render: r => (
                    <Badge color={r.dispute_raised_by_role === "tradesperson" ? "orange" : "green"}>
                      {r.dispute_raised_by_role === "tradesperson" ? "Trade" : "Customer"}
                    </Badge>
                  ) },
                  { key: "customer", label: "Customer", render: r => r.customer?.email || "—" },
                  { key: "trade", label: "Trade", render: r => r.trade?.business_name || r.trade?.email || "—" },
                  { key: "dispute_reason", label: "Reason", render: r => (
                    <span className="text-sm" title={r.dispute_reason}>{(r.dispute_reason || "").slice(0, 120)}{(r.dispute_reason || "").length > 120 ? "…" : ""}</span>
                  ) },
                  { key: "dispute_raised_at", label: "When", render: r => fmtDate(r.dispute_raised_at) },
                  { key: "actions", label: "", render: r => (
                    <button
                      onClick={async () => {
                        const notes = window.prompt("Resolution notes (optional):") ?? "";
                        try {
                          await api.post(`/admin/disputes/${r.job_id}/resolve`, { notes });
                          toast.success("Dispute resolved");
                          load("disputes");
                        } catch (e) { toast.error(formatApiError(e)); }
                      }}
                      className="px-3 py-1.5 rounded-md text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700"
                      data-testid={`resolve-dispute-${r.job_id}`}
                    >
                      Resolve
                    </button>
                  ) },
                ]}
              />
            </div>
          </div>
        )}


        {/* ASSETS / UPLOADER */}
        {tab === "assets" && (
          <AssetsTab assets={data.assets} onChange={() => load("assets")} />
        )}

        {/* INBOX (contact submissions) */}
        {tab === "inbox" && (
          <InboxTab inbox={data.inbox} onChange={() => load("inbox")} />
        )}

        {/* VOUCHERS (earned £20 Amazon vouchers) */}
        {tab === "vouchers" && (
          <VouchersTab vouchers={data.vouchers} onChange={() => load("vouchers")} />
        )}

        {/* FAQ MANAGER */}
        {tab === "faqs" && (
          <FAQsTab faqs={data.faqs} onChange={() => load("faqs")} />
        )}


        {tab === "signups" && (
          <div className="mt-6 brand-card p-4">
            <Table
              empty="No launch-list signups yet"
              rows={data.signups || []}
              columns={[
                { key: "email", label: "Email", render: r => <span className="font-semibold">{r.email}</span> },
                { key: "source", label: "Source" },
                { key: "created_at", label: "Signed up", render: r => fmtDate(r.created_at) },
              ]}
            />
            {(data.signups || []).length > 0 && (
              <button
                onClick={() => {
                  const csv = "email,signed_up_at,source\n" + (data.signups || []).map(s => `${s.email},${s.created_at},${s.source || ""}`).join("\n");
                  const blob = new Blob([csv], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url; a.download = `myhandyhub-launch-list-${Date.now()}.csv`;
                  a.click(); URL.revokeObjectURL(url);
                }}
                className="btn-ghost mt-4"
                data-testid="export-signups-csv"
              >Export CSV</button>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

function AnalyticsTab({ data, live, loc, mapData, onRefresh }) {
  React.useEffect(() => {
    if (!onRefresh) return;
    const id = setInterval(onRefresh, 15000);
    return () => clearInterval(id);
  }, [onRefresh]);

  if (!data) {
    return <div className="mt-6 brand-card p-6 text-[var(--text-muted)]">Loading analytics…</div>;
  }

  const fmtRef = (ref) => {
    if (!ref) return "Direct";
    try {
      const u = new URL(ref);
      return u.hostname.replace(/^www\./, "");
    } catch {
      return ref.slice(0, 40);
    }
  };

  // Convert 2-letter country code (e.g. "GB") to flag emoji (🇬🇧)
  const flag = (code) => {
    if (!code || code.length !== 2) return "";
    const cc = code.toUpperCase();
    const A = 0x1F1E6;
    return String.fromCodePoint(A + cc.charCodeAt(0) - 65, A + cc.charCodeAt(1) - 65);
  };

  const locLabel = (r) => {
    const bits = [];
    if (r.city) bits.push(r.city);
    if (r.country_code) bits.push(flag(r.country_code));
    return bits.join(" ") || (r.country_code ? flag(r.country_code) : "Unknown");
  };

  return (
    <div className="mt-6 space-y-4" data-testid="analytics-tab">
      {/* Live counter */}
      <div className="brand-card p-5 sm:p-6 flex flex-wrap items-center gap-5">
        <div className="flex items-center gap-3">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
          </span>
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--text-muted)] font-bold">Right now</div>
            <div className="font-display font-extrabold text-4xl" style={{ color: "var(--brand-green)" }} data-testid="live-visitors">
              {data.live_visitors}
            </div>
            <div className="text-sm text-[var(--text-muted)]">visitor{data.live_visitors === 1 ? "" : "s"} on the site</div>
          </div>
        </div>
        <div className="text-xs text-[var(--text-muted)] ml-auto">Refreshes every 15s</div>
      </div>

      {/* Headline numbers */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatBox label="Visitors today" value={data.visitors_today} sub={`${data.pageviews_today} page views`} />
        <StatBox label="This week" value={data.visitors_week} sub={`${data.pageviews_week} page views`} />
        <StatBox label="This month" value={data.visitors_month} />
        <StatBox label="Total registered" value={(live?.rows || []).filter(r => r.user_id).length > 0 ? "—" : "—"} sub="see Customers/Trades tabs" />
      </div>

      {/* Hourly chart */}
      {(data.hourly_24h || []).length > 0 && (
        <div className="brand-card p-5">
          <div className="font-display font-extrabold text-base mb-3" style={{ color: "var(--brand-green)" }}>Last 24 hours</div>
          <HourlyBars rows={data.hourly_24h} />
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Top pages */}
        <div className="brand-card p-5">
          <div className="font-display font-extrabold text-base mb-3" style={{ color: "var(--brand-green)" }}>Top pages (7d)</div>
          {(data.top_pages || []).length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">No data yet</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {data.top_pages.map((p) => (
                <li key={p.path} className="flex justify-between items-center gap-3">
                  <span className="font-mono text-xs truncate" title={p.path}>{p.path}</span>
                  <span className="text-[var(--text-muted)] font-semibold text-xs flex-shrink-0">{p.views} · {p.uniques} uniq</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Top referrers */}
        <div className="brand-card p-5">
          <div className="font-display font-extrabold text-base mb-3" style={{ color: "var(--brand-green)" }}>Top referrers (7d)</div>
          {(data.top_referrers || []).length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">No referrers yet — everyone&apos;s direct or from social apps</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {data.top_referrers.map((p) => (
                <li key={p.referrer} className="flex justify-between gap-3">
                  <span className="truncate text-[var(--text-main)]" title={p.referrer}>{fmtRef(p.referrer)}</span>
                  <span className="text-[var(--text-muted)] font-semibold text-xs">{p.views}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Device split */}
        <div className="brand-card p-5">
          <div className="font-display font-extrabold text-base mb-3" style={{ color: "var(--brand-green)" }}>Devices (7d)</div>
          {(data.device_split || []).length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">No data yet</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {data.device_split.map((d) => (
                <li key={d.device} className="flex justify-between gap-3">
                  <span>{d.device}</span>
                  <span className="text-[var(--text-muted)] font-semibold text-xs">{d.views}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Live feed */}
        <div className="brand-card p-5">
          <div className="font-display font-extrabold text-base mb-3" style={{ color: "var(--brand-green)" }}>Live feed (last 30 min)</div>
          {(live?.rows || []).length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">Nothing in the last 30 min</p>
          ) : (
            <ul className="space-y-2 text-xs max-h-72 overflow-y-auto" data-testid="live-feed">
              {live.rows.map((r) => (
                <li key={r.visit_id} className="flex justify-between gap-3 border-b border-[var(--border-stone)] pb-1.5">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono truncate text-[var(--text-main)]">{r.path}</div>
                    <div className="text-[var(--text-muted)]">
                      <span className="mr-1">{locLabel(r)}</span>
                      · {r.user_role || "visitor"} · {r.device || "Unknown"} · {r.browser || ""}
                    </div>
                  </div>
                  <div className="text-[var(--text-muted)] flex-shrink-0">{new Date(r.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Location breakdown */}
      <div className="grid lg:grid-cols-2 gap-4" data-testid="analytics-locations">
        <VisitorMap points={mapData?.points || []} />
        <div className="brand-card p-5">
          <div className="font-display font-extrabold text-base mb-3" style={{ color: "var(--brand-green)" }}>
            Top cities (7d)
          </div>
          {(loc?.top_cities || []).length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">No city data yet — will populate as real visitors arrive</p>
          ) : (
            <ul className="space-y-1.5 text-sm" data-testid="top-cities">
              {loc.top_cities.map((c, i) => (
                <li key={`${c.city}-${c.country_code}-${i}`} className="flex justify-between items-center gap-3">
                  <span className="truncate">
                    <span className="mr-1.5">{flag(c.country_code)}</span>
                    <span className="font-semibold text-[var(--text-main)]">{c.city}</span>
                    {c.region && c.region !== c.city && (
                      <span className="text-xs text-[var(--text-muted)] ml-1">· {c.region}</span>
                    )}
                  </span>
                  <span className="text-[var(--text-muted)] font-semibold text-xs flex-shrink-0">
                    {c.views} · {c.uniques} uniq
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="brand-card p-5">
          <div className="font-display font-extrabold text-base mb-3" style={{ color: "var(--brand-green)" }}>
            Top countries (7d)
          </div>
          {(loc?.top_countries || []).length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">No country data yet</p>
          ) : (
            <ul className="space-y-1.5 text-sm" data-testid="top-countries">
              {loc.top_countries.map((c, i) => (
                <li key={`${c.country_code}-${i}`} className="flex justify-between items-center gap-3">
                  <span>
                    <span className="mr-1.5 text-lg">{flag(c.country_code)}</span>
                    <span className="font-semibold text-[var(--text-main)]">{c.country_name || c.country_code}</span>
                  </span>
                  <span className="text-[var(--text-muted)] font-semibold text-xs">
                    {c.views} · {c.uniques} uniq
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, sub }) {
  return (
    <div className="brand-card p-4">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)]">{label}</div>
      <div className="font-display font-extrabold text-3xl mt-1" style={{ color: "var(--brand-green)" }}>{value}</div>
      {sub && <div className="text-xs text-[var(--text-muted)] mt-0.5">{sub}</div>}
    </div>
  );
}

function HourlyBars({ rows }) {
  const max = Math.max(1, ...rows.map((r) => r.views || 0));
  return (
    <div className="flex items-end gap-1 h-24" data-testid="hourly-bars">
      {rows.map((r) => {
        const pct = ((r.views || 0) / max) * 100;
        const h = r.hour?.slice(-5) || "";
        return (
          <div key={r.hour} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${r.hour}: ${r.views} views`}>
            <div className="w-full rounded-t" style={{ height: `${pct}%`, minHeight: "2px", background: "var(--brand-orange)" }} />
            <div className="text-[9px] text-[var(--text-muted)] truncate">{h}</div>
          </div>
        );
      })}
    </div>
  );
}



function AssetsTab({ assets, onChange }) {
  const [uploading, setUploading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [recent, setRecent] = React.useState(null);
  const fileRef = React.useRef(null);
  const dropRef = React.useRef(null);
  const [dragOver, setDragOver] = React.useState(false);

  const apiBase = api.defaults.baseURL || "";
  const origin = apiBase.replace(/\/api\/?$/, "");

  const fullUrl = (storagePath) => `${origin}/api/files/${storagePath}`;

  const doUpload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File too large — max 8 MB per image");
      return;
    }
    setUploading(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/uploads/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      const url = fullUrl(data.path);
      setRecent({ url, path: data.path, name: file.name, size: data.size, content_type: data.content_type });
      toast.success("Uploaded! URL copied to clipboard");
      try { await navigator.clipboard.writeText(url); } catch (e) { console.error("clipboard", e); }
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false); setProgress(0);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch (e) {
      console.error("clipboard", e);
      toast.error("Copy failed — select & copy manually");
    }
  };

  const deleteAsset = async (path) => {
    if (!window.confirm("Delete this asset? This can't be undone — and any page using its URL will break.")) return;
    try {
      await api.delete(`/uploads/${path}`);
      toast.success("Deleted");
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) doUpload(f);
  };

  const fmtBytes = (n) => {
    if (!n && n !== 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  };

  const rows = assets?.rows || [];

  return (
    <div className="mt-6 space-y-5" data-testid="assets-tab">
      <div className="brand-card p-5">
        <div className="font-display font-extrabold text-lg" style={{ color: "var(--brand-green)" }}>Asset uploader</div>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          Upload images (PNG, JPG, WEBP, GIF — up to 8 MB) and get a permanent URL you can paste anywhere — emails, social posts, ads. Files are served from <code className="text-xs bg-[#FAF8F2] px-1.5 py-0.5 rounded">myhandyhub.co.uk/api/files/…</code> once you redeploy.
        </p>

        <div
          ref={dropRef}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => !uploading && fileRef.current?.click()}
          className={`mt-4 border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
            dragOver ? "border-[var(--brand-orange)] bg-[rgba(226,109,41,0.06)]" : "border-[var(--border-stone)] hover:border-[var(--brand-green)] hover:bg-[#FAF8F2]"
          }`}
          data-testid="asset-dropzone"
        >
          <UploadSimple size={32} weight="duotone" className="mx-auto text-[var(--brand-green)]" />
          <div className="mt-2 font-display font-extrabold text-base" style={{ color: "var(--brand-green)" }}>
            {uploading ? `Uploading… ${progress}%` : "Drop an image here or click to pick"}
          </div>
          <div className="text-xs text-[var(--text-muted)] mt-1">PNG · JPG · WEBP · GIF · HEIC — max 8 MB</div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            disabled={uploading}
            onChange={(e) => doUpload(e.target.files?.[0])}
            data-testid="asset-file-input"
          />
        </div>

        {recent && (
          <div className="mt-4 rounded-lg p-4" style={{ background: "#ECFDF5" }} data-testid="asset-recent">
            <div className="flex items-start gap-3">
              <img src={recent.url} alt={recent.name} className="w-16 h-16 object-cover rounded-md flex-shrink-0 bg-white" />
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-wide font-bold text-emerald-700">Just uploaded · copied to clipboard</div>
                <div className="font-semibold text-sm truncate" style={{ color: "#065F46" }}>{recent.name}</div>
                <div className="text-xs text-[var(--text-muted)]">{fmtBytes(recent.size)} · {recent.content_type}</div>
                <div className="flex gap-2 mt-2">
                  <input
                    readOnly
                    value={recent.url}
                    className="brand-input flex-1 text-xs font-mono"
                    onFocus={(e) => e.target.select()}
                    data-testid="asset-recent-url"
                  />
                  <button onClick={() => copy(recent.url)} className="btn-ghost text-xs" data-testid="asset-recent-copy">
                    <Copy size={14} weight="bold" className="inline mr-1" /> Copy
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="brand-card p-4">
        <div className="flex items-center justify-between mb-3 px-1">
          <div className="font-display font-extrabold text-base" style={{ color: "var(--brand-green)" }}>
            All uploads ({rows.length})
          </div>
          <button onClick={onChange} className="btn-ghost text-xs" data-testid="assets-refresh">Refresh</button>
        </div>

        {rows.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)] py-6 text-center">No uploads yet — drop a file above to get started.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {rows.map((r) => {
              const url = fullUrl(r.storage_path);
              const isImage = (r.content_type || "").startsWith("image/");
              return (
                <div key={r.storage_path} className="rounded-lg border border-[var(--border-stone)] p-3 flex gap-3 hover:shadow-sm transition-shadow" data-testid={`asset-row-${r.storage_path}`}>
                  <div className="w-16 h-16 flex-shrink-0 rounded-md overflow-hidden bg-[#FAF8F2] flex items-center justify-center">
                    {isImage ? (
                      <img src={url} alt={r.original_filename || ""} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <ImageIcon size={28} className="text-[var(--text-muted)]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate" style={{ color: "var(--brand-green)" }} title={r.original_filename}>
                      {r.original_filename || r.storage_path.split("/").pop()}
                    </div>
                    <div className="text-[11px] text-[var(--text-muted)]">
                      {fmtBytes(r.size)} · {fmtDate(r.created_at)}
                    </div>
                    <div className="flex gap-1 mt-1.5">
                      <button onClick={() => copy(url)} className="px-2 py-1 rounded text-[11px] font-semibold bg-[var(--brand-green)] text-white hover:opacity-90" data-testid={`asset-copy-${r.storage_path}`}>
                        <Copy size={11} weight="bold" className="inline mr-1" /> Copy URL
                      </button>
                      <a href={url} target="_blank" rel="noreferrer" className="px-2 py-1 rounded text-[11px] font-semibold bg-[#FAF8F2] hover:bg-stone-200 text-[var(--brand-green)]">
                        Open
                      </a>
                      <button onClick={() => deleteAsset(r.storage_path)} className="px-2 py-1 rounded text-[11px] font-semibold bg-red-50 text-red-700 hover:bg-red-100" data-testid={`asset-delete-${r.storage_path}`}>
                        <Trash size={11} weight="bold" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const FAQ_CATS = [
  { id: "customer", label: "Customers" },
  { id: "trade", label: "Tradespeople" },
  { id: "general", label: "General" },
];

function FAQsTab({ faqs, onChange }) {
  const [editing, setEditing] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [filterCat, setFilterCat] = React.useState("all");
  const rows = faqs?.rows || [];

  const visible = filterCat === "all" ? rows : rows.filter((r) => r.category === filterCat);

  const startCreate = () => setEditing({
    category: filterCat === "all" ? "customer" : filterCat,
    question: "",
    answer: "",
    order: visible.length,
    is_published: true,
  });
  const startEdit = (r) => setEditing({ ...r });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    const q = (editing.question || "").trim();
    const a = (editing.answer || "").trim();
    if (q.length < 3 || a.length < 3) {
      toast.error("Question & answer must each be at least 3 characters");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        category: editing.category,
        question: q,
        answer: a,
        order: Number(editing.order) || 0,
        is_published: !!editing.is_published,
      };
      if (editing.faq_id) {
        await api.put(`/admin/faqs/${editing.faq_id}`, payload);
        toast.success("FAQ updated");
      } else {
        await api.post(`/admin/faqs`, payload);
        toast.success("FAQ created");
      }
      setEditing(null);
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (r) => {
    try {
      await api.put(`/admin/faqs/${r.faq_id}`, { is_published: !r.is_published });
      toast.success(r.is_published ? "Hidden from public FAQ" : "Published");
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete this FAQ?\n\n"${r.question}"\n\nThis can't be undone.`)) return;
    try {
      await api.delete(`/admin/faqs/${r.faq_id}`);
      toast.success("Deleted");
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-6 space-y-4" data-testid="faqs-tab">
      <div className="brand-card p-5 flex flex-wrap items-center gap-3 justify-between">
        <div>
          <div className="font-display font-extrabold text-lg" style={{ color: "var(--brand-green)" }}>FAQ manager</div>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Add, edit, hide or delete questions shown at <Link to="/faq" className="underline">/faq</Link>. Changes go live immediately.
          </p>
        </div>
        <button onClick={startCreate} className="btn-primary inline-flex items-center gap-2" data-testid="faq-new-btn">
          <Plus size={14} weight="bold" /> New FAQ
        </button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="faq-cat-filter">
        {[{ id: "all", label: `All (${rows.length})` }, ...FAQ_CATS.map((c) => ({
          id: c.id, label: `${c.label} (${rows.filter((r) => r.category === c.id).length})`
        }))].map((c) => (
          <button
            key={c.id}
            onClick={() => setFilterCat(c.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
              filterCat === c.id
                ? "bg-[var(--brand-green)] text-white"
                : "bg-white border border-[var(--border-stone)] text-[var(--text-muted)] hover:text-[var(--brand-green)]"
            }`}
            data-testid={`faq-filter-${c.id}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {editing && (
        <div className="brand-card p-5 border-2" style={{ borderColor: "var(--brand-orange)" }} data-testid="faq-editor">
          <div className="font-display font-extrabold text-base mb-3" style={{ color: "var(--brand-green)" }}>
            {editing.faq_id ? "Edit FAQ" : "New FAQ"}
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">Category</label>
              <select
                value={editing.category}
                onChange={(e) => setEditing((s) => ({ ...s, category: e.target.value }))}
                className="brand-input w-full mt-1"
                data-testid="faq-editor-category"
              >
                {FAQ_CATS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">Order</label>
              <input
                type="number"
                value={editing.order}
                onChange={(e) => setEditing((s) => ({ ...s, order: e.target.value }))}
                className="brand-input w-full mt-1"
                data-testid="faq-editor-order"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.is_published}
                  onChange={(e) => setEditing((s) => ({ ...s, is_published: e.target.checked }))}
                  data-testid="faq-editor-published"
                />
                <span className="font-semibold" style={{ color: "var(--brand-green)" }}>Published (visible on /faq)</span>
              </label>
            </div>
          </div>
          <div className="mt-3">
            <label className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">Question</label>
            <input
              value={editing.question}
              onChange={(e) => setEditing((s) => ({ ...s, question: e.target.value }))}
              maxLength={300}
              placeholder="e.g. How do I cancel my subscription?"
              className="brand-input w-full mt-1"
              data-testid="faq-editor-question"
            />
          </div>
          <div className="mt-3">
            <label className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">Answer</label>
            <textarea
              value={editing.answer}
              onChange={(e) => setEditing((s) => ({ ...s, answer: e.target.value }))}
              maxLength={4000}
              rows={6}
              placeholder="Write a clear, friendly answer. Line breaks are preserved."
              className="brand-input w-full mt-1 font-sans"
              data-testid="faq-editor-answer"
            />
            <div className="text-xs text-[var(--text-muted)] mt-1 text-right">{(editing.answer || "").length} / 4000</div>
          </div>
          <div className="mt-3 flex gap-2 justify-end">
            <button onClick={cancel} disabled={saving} className="btn-ghost" data-testid="faq-editor-cancel">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary" data-testid="faq-editor-save">
              {saving ? "Saving…" : editing.faq_id ? "Save changes" : "Create FAQ"}
            </button>
          </div>
        </div>
      )}

      <div className="brand-card p-4">
        {visible.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)] py-6 text-center">No FAQs in this category yet. Click <strong>New FAQ</strong> to add one.</p>
        ) : (
          <ul className="divide-y divide-[var(--border-stone)]" data-testid="faq-admin-list">
            {visible.map((r) => (
              <li key={r.faq_id} className="py-3 flex flex-wrap items-start gap-3" data-testid={`faq-row-${r.faq_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Badge color={r.category === "trade" ? "orange" : r.category === "general" ? "grey" : "green"}>
                      {FAQ_CATS.find((c) => c.id === r.category)?.label || r.category}
                    </Badge>
                    {!r.is_published && <Badge color="grey">Hidden</Badge>}
                    <span className="text-[10px] text-[var(--text-muted)] font-mono">order {r.order}</span>
                  </div>
                  <div className="font-semibold text-sm" style={{ color: "var(--brand-green)" }}>{r.question}</div>
                  <div className="text-xs text-[var(--text-muted)] mt-0.5 line-clamp-2 whitespace-pre-wrap">{r.answer}</div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button
                    onClick={() => togglePublish(r)}
                    title={r.is_published ? "Hide from public FAQ" : "Publish"}
                    className="px-2 py-1.5 rounded-md text-xs font-semibold bg-[#FAF8F2] hover:bg-stone-200 text-[var(--brand-green)]"
                    data-testid={`faq-toggle-${r.faq_id}`}
                  >
                    {r.is_published ? <Eye size={14} weight="bold" /> : <EyeSlash size={14} weight="bold" />}
                  </button>
                  <button
                    onClick={() => startEdit(r)}
                    className="px-2 py-1.5 rounded-md text-xs font-semibold bg-[#FAF8F2] hover:bg-stone-200 text-[var(--brand-green)]"
                    data-testid={`faq-edit-${r.faq_id}`}
                  >
                    <PencilSimple size={14} weight="bold" />
                  </button>
                  <button
                    onClick={() => remove(r)}
                    className="px-2 py-1.5 rounded-md text-xs font-semibold bg-red-50 hover:bg-red-100 text-red-700"
                    data-testid={`faq-delete-${r.faq_id}`}
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


const INBOX_STATUSES = [
  { id: "all", label: "All" },
  { id: "new", label: "New" },
  { id: "replied", label: "Replied" },
  { id: "archived", label: "Archived" },
];

function InboxTab({ inbox, onChange }) {
  const [filter, setFilter] = React.useState("all");
  const [selected, setSelected] = React.useState(null);
  const [reply, setReply] = React.useState("");
  const [signerName, setSignerName] = React.useState("Adeel Shah");
  const [signerTitle, setSignerTitle] = React.useState("Founder & CEO");
  const [sending, setSending] = React.useState(false);

  const rows = inbox?.rows || [];
  const newCount = inbox?.new_count || 0;
  const visible = filter === "all" ? rows : rows.filter((r) => r.status === filter);

  const openMessage = (r) => {
    setSelected(r);
    setReply("");
  };

  const back = () => { setSelected(null); setReply(""); };

  const sendReply = async () => {
    if (reply.trim().length < 5) {
      toast.error("Write a reply of at least a sentence first.");
      return;
    }
    setSending(true);
    try {
      await api.post(`/admin/contact/${selected.msg_id}/reply`, {
        body: reply.trim(),
        signer_name: signerName.trim() || null,
        signer_title: signerTitle.trim() || null,
      });
      toast.success(`Reply sent to ${selected.email}`);
      setReply("");
      setSelected(null);
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (msg_id, status) => {
    try {
      await api.put(`/admin/contact/${msg_id}`, { status });
      toast.success(status === "archived" ? "Archived" : "Marked as " + status);
      onChange?.();
      if (selected?.msg_id === msg_id) setSelected((s) => s ? { ...s, status } : s);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (msg_id) => {
    if (!window.confirm("Delete this message permanently?")) return;
    try {
      await api.delete(`/admin/contact/${msg_id}`);
      toast.success("Deleted");
      onChange?.();
      if (selected?.msg_id === msg_id) back();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Detail view
  if (selected) {
    const statusBadge = selected.status === "new" ? "orange" : selected.status === "replied" ? "green" : "grey";
    return (
      <div className="mt-6 space-y-4" data-testid="inbox-detail">
        <button onClick={back} className="btn-ghost text-sm inline-flex items-center gap-1" data-testid="inbox-back">
          <ArrowLeft size={14} weight="bold" /> Back to inbox
        </button>

        <div className="brand-card p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <Badge color={statusBadge}>{selected.status}</Badge>
                <Badge color="grey">{selected.role}</Badge>
                <span className="text-xs text-[var(--text-muted)]">{fmtDate(selected.created_at)}</span>
              </div>
              <h2 className="font-display font-extrabold text-xl" style={{ color: "var(--brand-green)" }}>
                {selected.subject_label}
              </h2>
              <div className="text-sm text-[var(--text-muted)] mt-0.5">
                <strong style={{ color: "var(--text-main)" }}>{selected.name}</strong>{" · "}
                <a href={`mailto:${selected.email}`} className="hover:underline" style={{ color: "var(--brand-orange)" }}>{selected.email}</a>
                {selected.phone && <> · {selected.phone}</>}
              </div>
            </div>
            <div className="flex gap-1">
              {selected.status !== "archived" && (
                <button onClick={() => setStatus(selected.msg_id, "archived")} className="px-2.5 py-1.5 rounded-md text-xs font-semibold bg-[#FAF8F2] hover:bg-stone-200 text-[var(--brand-green)]" data-testid="inbox-archive">
                  <Archive size={14} weight="bold" className="inline mr-1" /> Archive
                </button>
              )}
              <button onClick={() => remove(selected.msg_id)} className="px-2.5 py-1.5 rounded-md text-xs font-semibold bg-red-50 hover:bg-red-100 text-red-700" data-testid="inbox-delete">
                <Trash size={14} weight="bold" />
              </button>
            </div>
          </div>

          <div className="mt-5 p-4 rounded-lg" style={{ background: "#FAF8F2" }}>
            <div className="text-xs uppercase tracking-wide font-bold text-[var(--text-muted)] mb-2">Message</div>
            <div className="text-sm whitespace-pre-wrap leading-relaxed">{selected.message}</div>
          </div>

          {selected.last_reply_body && (
            <div className="mt-4 p-4 rounded-lg border" style={{ borderColor: "rgba(17,82,59,0.2)", background: "rgba(17,82,59,0.04)" }}>
              <div className="text-xs uppercase tracking-wide font-bold mb-2" style={{ color: "var(--brand-green)" }}>
                Your last reply ({fmtDate(selected.replied_at)})
              </div>
              <div className="text-sm whitespace-pre-wrap leading-relaxed">{selected.last_reply_body}</div>
            </div>
          )}
        </div>

        {/* Reply composer */}
        <div className="brand-card p-6">
          <div className="font-display font-extrabold text-lg" style={{ color: "var(--brand-green)" }}>
            <PaperPlaneTilt size={18} weight="duotone" className="inline mr-2" style={{ color: "var(--brand-orange)" }} />
            Reply to {selected.name.split(" ")[0]}
          </div>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            Sent from <code className="text-[11px] bg-[#FAF8F2] px-1 py-0.5 rounded">noreply@myhandyhub.co.uk</code>
            {" with branded signature. Replies from the customer come to "}
            <code className="text-[11px] bg-[#FAF8F2] px-1 py-0.5 rounded">hello@myhandyhub.co.uk</code>.
          </p>

          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">Sign as (name)</label>
              <input value={signerName} onChange={(e) => setSignerName(e.target.value)} className="brand-input w-full mt-1" data-testid="inbox-signer-name" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">Title</label>
              <input value={signerTitle} onChange={(e) => setSignerTitle(e.target.value)} className="brand-input w-full mt-1" data-testid="inbox-signer-title" />
            </div>
          </div>

          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={8}
            placeholder={`Hi ${selected.name.split(" ")[0]},\n\nThanks for reaching out…`}
            className="brand-input w-full mt-3 font-sans"
            maxLength={8000}
            data-testid="inbox-reply-body"
          />
          <div className="text-xs text-[var(--text-muted)] mt-1 text-right">{reply.length} / 8000</div>

          <div className="mt-3 flex gap-2 justify-end">
            <button onClick={back} disabled={sending} className="btn-ghost" data-testid="inbox-cancel">Cancel</button>
            <button onClick={sendReply} disabled={sending} className="btn-primary inline-flex items-center gap-2" data-testid="inbox-send">
              <PaperPlaneTilt size={14} weight="bold" />
              {sending ? "Sending…" : "Send reply"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="mt-6 space-y-4" data-testid="inbox-tab">
      <div className="brand-card p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display font-extrabold text-lg flex items-center gap-2" style={{ color: "var(--brand-green)" }}>
            Inbox
            {newCount > 0 && <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white" style={{ background: "var(--brand-orange)" }}>{newCount} new</span>}
          </div>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Contact-form submissions from <Link to="/contact" className="underline">/contact</Link>. Reply with one click — branded signature is added automatically.
          </p>
        </div>
        <button onClick={onChange} className="btn-ghost text-xs" data-testid="inbox-refresh">Refresh</button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="inbox-filter">
        {INBOX_STATUSES.map((s) => {
          const n = s.id === "all" ? rows.length : rows.filter((r) => r.status === s.id).length;
          return (
            <button
              key={s.id}
              onClick={() => setFilter(s.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                filter === s.id
                  ? "bg-[var(--brand-green)] text-white"
                  : "bg-white border border-[var(--border-stone)] text-[var(--text-muted)] hover:text-[var(--brand-green)]"
              }`}
              data-testid={`inbox-filter-${s.id}`}
            >
              {s.label} ({n})
            </button>
          );
        })}
      </div>

      <div className="brand-card p-2">
        {visible.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)] py-10 text-center">
            {rows.length === 0 ? "No contact submissions yet." : "Nothing in this status."}
          </p>
        ) : (
          <ul className="divide-y divide-[var(--border-stone)]">
            {visible.map((r) => (
              <li
                key={r.msg_id}
                onClick={() => openMessage(r)}
                className="p-3 sm:p-4 hover:bg-[#FAF8F2] cursor-pointer transition-colors flex flex-wrap items-start gap-3"
                data-testid={`inbox-row-${r.msg_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {r.status === "new" && <span className="w-2 h-2 rounded-full" style={{ background: "var(--brand-orange)" }} />}
                    <span className="font-semibold text-sm" style={{ color: "var(--brand-green)" }}>{r.name}</span>
                    <span className="text-xs text-[var(--text-muted)]">· {r.email}</span>
                    <Badge color={r.role === "tradesperson" ? "orange" : r.role === "homeowner" ? "green" : "grey"}>{r.role}</Badge>
                  </div>
                  <div className="text-sm font-semibold" style={{ color: "var(--text-main)" }}>{r.subject_label}</div>
                  <div className="text-xs text-[var(--text-muted)] mt-0.5 line-clamp-1">{r.message}</div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className="text-xs text-[var(--text-muted)]">{fmtDate(r.created_at)}</span>
                  <Badge color={r.status === "new" ? "orange" : r.status === "replied" ? "green" : "grey"}>{r.status}</Badge>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


function VouchersTab({ vouchers, onChange }) {
  const [filter, setFilter] = React.useState("issued");
  const [busy, setBusy] = React.useState(null);
  const rows = vouchers?.rows || [];
  const counts = vouchers?.counts || { issued: 0, sent: 0, redeemed: 0 };

  const visible = filter === "all" ? rows : rows.filter((r) => r.voucher_status === filter);

  const updateStatus = async (r, status, extra = {}) => {
    setBusy(r.waitlist_id);
    try {
      await api.put(`/admin/waitlist/vouchers/${r.waitlist_id}`, { status, ...extra });
      toast.success(status === "sent" ? "Marked as sent" : status === "redeemed" ? "Marked as redeemed" : "Updated");
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const markSent = async (r) => {
    const code = window.prompt(`Paste the £20 Amazon voucher code for ${r.first_name} (${r.email}). Leave blank to skip storing the code.`);
    if (code === null) return; // cancel
    await updateStatus(r, "sent", { voucher_code: code || undefined });
  };

  const copyEmail = async (email) => {
    try { await navigator.clipboard.writeText(email); toast.success("Email copied"); } catch (e) { console.error("clipboard", e); toast.error("Copy failed"); }
  };

  return (
    <div className="mt-6 space-y-4" data-testid="vouchers-tab">
      <div className="brand-card p-5 flex flex-wrap items-center gap-3 justify-between">
        <div>
          <div className="font-display font-extrabold text-lg flex items-center gap-2" style={{ color: "var(--brand-green)" }}>
            <Gift size={20} weight="duotone" style={{ color: "var(--brand-orange)" }} />
            £20 Amazon vouchers earned
          </div>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Customers who signed up as one of the first 100 <strong>and</strong> completed their first job. Send them a £20 Amazon.co.uk voucher code within 3 working days, then mark as sent.
          </p>
        </div>
        <button onClick={onChange} className="btn-ghost text-xs" data-testid="vouchers-refresh">Refresh</button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <VStatCard label="To send" value={counts.issued} accent="orange" />
        <VStatCard label="Sent" value={counts.sent} accent="green" />
        <VStatCard label="Redeemed" value={counts.redeemed} accent="grey" />
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { id: "issued", label: `To send (${counts.issued})` },
          { id: "sent", label: `Sent (${counts.sent})` },
          { id: "redeemed", label: `Redeemed (${counts.redeemed})` },
          { id: "all", label: `All (${rows.length})` },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
              filter === f.id
                ? "bg-[var(--brand-green)] text-white"
                : "bg-white border border-[var(--border-stone)] text-[var(--text-muted)] hover:text-[var(--brand-green)]"
            }`}
            data-testid={`voucher-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="brand-card p-2">
        {visible.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)] py-10 text-center">
            {filter === "issued" ? "🎉 No vouchers waiting to be sent right now." : "Nothing in this status."}
          </p>
        ) : (
          <ul className="divide-y divide-[var(--border-stone)]">
            {visible.map((r) => (
              <li key={r.waitlist_id} className="p-3 sm:p-4 flex flex-wrap items-start gap-3" data-testid={`voucher-row-${r.waitlist_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge color={r.voucher_status === "issued" ? "orange" : r.voucher_status === "sent" ? "green" : "grey"}>{r.voucher_status}</Badge>
                    <span className="text-[11px] font-mono text-[var(--text-muted)]">#{r.position}</span>
                    <span className="font-semibold text-sm" style={{ color: "var(--brand-green)" }}>{r.first_name}</span>
                    <button onClick={() => copyEmail(r.email)} className="text-xs text-[var(--brand-orange)] hover:underline" data-testid={`voucher-email-${r.waitlist_id}`}>
                      {r.email}
                    </button>
                    <span className="text-xs text-[var(--text-muted)]">· {r.postcode}</span>
                  </div>
                  <div className="text-xs text-[var(--text-muted)] mt-1">
                    Job posted: {fmtDate(r.first_job_posted_at)} · Quote accepted: {fmtDate(r.first_quote_accepted_at)} · Earned: <strong>{fmtDate(r.voucher_issued_at)}</strong>
                    {r.voucher_sent_at && <> · Sent: {fmtDate(r.voucher_sent_at)}</>}
                  </div>
                  {r.voucher_code && (
                    <div className="text-xs mt-1">
                      Code: <code className="bg-[#FAF8F2] px-1.5 py-0.5 rounded font-mono">{r.voucher_code}</code>
                    </div>
                  )}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {r.voucher_status === "issued" && (
                    <button
                      onClick={() => markSent(r)}
                      disabled={busy === r.waitlist_id}
                      className="px-3 py-1.5 rounded-md text-xs font-bold text-white"
                      style={{ background: "var(--brand-green)" }}
                      data-testid={`voucher-mark-sent-${r.waitlist_id}`}
                    >
                      <Check size={12} weight="bold" className="inline mr-1" /> Mark sent
                    </button>
                  )}
                  {r.voucher_status === "sent" && (
                    <button
                      onClick={() => updateStatus(r, "redeemed")}
                      disabled={busy === r.waitlist_id}
                      className="px-3 py-1.5 rounded-md text-xs font-bold text-white bg-stone-500 hover:bg-stone-600"
                      data-testid={`voucher-mark-redeemed-${r.waitlist_id}`}
                    >
                      Mark redeemed
                    </button>
                  )}
                  {r.voucher_status !== "issued" && (
                    <button
                      onClick={() => updateStatus(r, "issued")}
                      disabled={busy === r.waitlist_id}
                      className="px-2 py-1.5 rounded-md text-xs font-semibold bg-[#FAF8F2] hover:bg-stone-200 text-[var(--brand-green)]"
                      title="Move back to 'to send'"
                      data-testid={`voucher-revert-${r.waitlist_id}`}
                    >
                      ↺
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function VStatCard({ label, value, accent }) {
  const color = accent === "orange" ? "var(--brand-orange)" : accent === "green" ? "var(--brand-green)" : "#4A5D54";
  return (
    <div className="brand-card p-4 text-center">
      <div className="text-xs uppercase tracking-wide font-bold text-[var(--text-muted)]">{label}</div>
      <div className="font-display font-extrabold text-3xl mt-1" style={{ color }}>{value}</div>
    </div>
  );
}

